import { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import RealApp from "@/app/RealApp";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { ProtoContext } from "@/proto/ProtoContext";
import { LEADERS, STATUS_SETS, leaderName } from "@/proto/data";
import { Toolbar } from "@/proto/Toolbar";
import { BottomNav, Sidebar } from "@/proto/Nav";
import { ConceptNotes } from "@/proto/ConceptNotes";
import LogCare from "@/proto/LogCare";
import Today from "@/proto/screens/Today";
import People from "@/proto/screens/People";
import Person from "@/proto/screens/Person";
import Overview from "@/proto/screens/PastorOverview";
import ListSheet from "@/proto/ListSheet";
import { cn } from "@/lib/utils";

const SCREENS = { today: Today, people: People, person: Person, overview: Overview };

const useNarrow = () => {
  const [narrow, setNarrow] = useState(window.innerWidth < 1024);
  useEffect(() => {
    const on = () => setNarrow(window.innerWidth < 1024);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return narrow;
};

const AppSurface = ({ isPhone, screen }) => {
  const Screen = SCREENS[screen];
  return (
    <div className="relative h-full w-full overflow-hidden bg-paper text-ink flex" data-testid="app-surface">
      {!isPhone && <Sidebar />}
      <div className="flex-1 h-full overflow-y-auto" key={screen} data-testid="app-scroll"><Screen /></div>
      {isPhone && <BottomNav />}
      <LogCare />
      <ListSheet />
    </div>
  );
};

function Prototype() {
  const narrow = useNarrow();
  const [screen, setScreen] = useState("today");
  const [history, setHistory] = useState([]);
  const [role, setRoleState] = useState("leader");
  const [device, setDevice] = useState("phone");
  const [palette, setPalette] = useState("moss");
  const [personId, setPersonId] = useState("harold");
  const [completed, setCompleted] = useState({});
  const [sheet, setSheet] = useState({ open: false });
  const [notesOpen, setNotesOpen] = useState(false);
  const [rhythm, setRhythm] = useState(30);
  const [assigned, setAssigned] = useState({});
  const [sent, setSent] = useState({});
  const [wording, setWording] = useState("A");
  const [list, setList] = useState({ open: false, key: null });
  const isPhone = narrow || device === "phone";

  useEffect(() => {
    document.body.className = `pal-${palette}`;
  }, [palette]);

  const ctx = useMemo(() => {
    const go = (s) => { setHistory((h) => [...h, screen]); setScreen(s); if (s === "overview") setRoleState("pastor"); };
    return {
      screen, role, isPhone, device, setDevice, narrow, palette, setPalette, personId, completed, sheet, notesOpen, setNotesOpen, rhythm, setRhythm, assigned, sent, wording, setWording, list,
      status: STATUS_SETS[wording],
      openList: (key) => setList({ open: true, key }),
      closeList: () => setList((l) => ({ ...l, open: false })),
      assign: (item, lid) => {
        setAssigned((a) => ({ ...a, [item.id]: lid }));
        toast.success(`${leaderName(lid)} is now caring for ${item.name || `${item.first} ${item.last}`}`, { description: "It will appear on their Today list." });
      },
      remind: (id, lid) => {
        setSent((s) => ({ ...s, [id]: true }));
        toast.success(`A gentle reminder was sent to ${LEADERS[lid].first}`);
      },
      me: role === "pastor" ? LEADERS.david : LEADERS.monica,
      current: sheet.open ? "log" : screen,
      go,
      back: () => { setScreen(history[history.length - 1] || "today"); setHistory((h) => h.slice(0, -1)); },
      openPerson: (id) => { setPersonId(id); go("person"); },
      openLog: (id, method) => setSheet({ open: true, personId: id, method }),
      closeLog: () => setSheet({ open: false }),
      completeCare: (id, info) => setCompleted((c) => ({ ...c, [id]: info })),
      setRole: (r) => { setRoleState(r); setCompleted({}); if (r === "leader" && screen === "overview") setScreen("today"); if (r === "pastor" && screen === "person" && !["harold", "walter", "naomi"].includes(personId)) setPersonId("harold"); },
      soon: (label) => toast(`${label} isn't part of this concept round`, { description: "Today, People, Person, Log Care and Overview are." }),
      jump: (id) => {
        setSheet({ open: false });
        setList((l) => ({ ...l, open: false }));
        if (id === "log") { setScreen(screen === "overview" ? "today" : screen); setSheet({ open: true, personId: screen === "person" ? personId : "harold" }); return; }
        if (id === "person") setPersonId("harold");
        go(id);
      },
    };
  }, [screen, history, role, isPhone, device, narrow, palette, personId, completed, sheet, notesOpen, rhythm, assigned, sent, wording, list]);

  const notesId = ctx.current;
  return (
    <ProtoContext.Provider value={ctx}>
      <div className={cn("pal-" + palette, "min-h-screen bg-canvas font-sans text-ink")} data-testid="proto-root">
        <Toolbar />
        {narrow ? (
          <div className="h-[calc(100dvh-56px)]"><AppSurface isPhone screen={screen} /></div>
        ) : device === "phone" ? (
          <div className="flex justify-center items-start gap-16 px-10 py-8">
            <div className="w-[390px] h-[min(844px,calc(100vh-120px))] min-h-[640px] rounded-[48px] border-[10px] border-[#1c1a17] shadow-device overflow-hidden shrink-0" data-testid="phone-frame">
              <AppSurface isPhone screen={screen} />
            </div>
            <div className="w-[420px] pt-6 max-h-[calc(100vh-120px)] overflow-y-auto"><ConceptNotes id={notesId} /></div>
          </div>
        ) : (
          <div className="h-[calc(100vh-56px)]"><AppSurface isPhone={false} screen={screen} /></div>
        )}
        {notesOpen && (narrow || device === "desktop") && (
          <div className="fixed right-0 top-14 bottom-0 z-[60] w-full max-w-[440px] bg-paper border-l border-line shadow-device overflow-y-auto p-7" data-testid="concept-notes-drawer">
            <button onClick={() => setNotesOpen(false)} className="mb-4 text-ink-soft underline underline-offset-4" data-testid="concept-notes-close">Close notes</button>
            <ConceptNotes id={notesId} />
          </div>
        )}
        <Toaster position="top-center" richColors={false} />
      </div>
    </ProtoContext.Provider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/prototype" element={<Prototype />} />
        <Route path="*" element={<ClerkAuthProvider><RealApp /><Toaster position="top-center" /></ClerkAuthProvider>} />
      </Routes>
    </BrowserRouter>
  );
}
